import type { SelectOption } from '../types';

const PRESERVED_PRINCIPAL_SELECTION_PREFIX = '__preserved_principal__:';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const toText = (value: unknown): string => String(value ?? '').trim();

export const resolvePrimaryAddressLabel = (organization: unknown): string => {
  const ext = asRecord(asRecord(organization).ext);
  return toText(ext.primaryAddressLabel) || 'Main Campus';
};

export const encodePreservedPrincipalSelection = (principalName: unknown): string => {
  const normalized = toText(principalName);
  return normalized ? `${PRESERVED_PRINCIPAL_SELECTION_PREFIX}${normalized}` : '';
};

export const isPreservedPrincipalSelection = (value: unknown): boolean =>
  toText(value).startsWith(PRESERVED_PRINCIPAL_SELECTION_PREFIX);

export const decodePreservedPrincipalSelection = (value: unknown): string | null => {
  const normalized = toText(value);
  if (!normalized.startsWith(PRESERVED_PRINCIPAL_SELECTION_PREFIX)) {
    return null;
  }
  return normalized.slice(PRESERVED_PRINCIPAL_SELECTION_PREFIX.length).trim() || null;
};

export const resolveInitialPrincipalSelection = (division: unknown): string => {
  const record = asRecord(division);
  return toText(record.principalId) || encodePreservedPrincipalSelection(asRecord(record.ext).principalName);
};

export const buildPrincipalOptionsForDivision = ({
  users,
  division,
}: {
  users: Array<{ id: string; name: string }>;
  division: unknown;
}): SelectOption[] => {
  const options: SelectOption[] = [
    { label: 'Select Principal', value: '' },
    ...users.map((user) => ({ label: user.name, value: user.id })),
  ];

  const preservedSelection = encodePreservedPrincipalSelection(asRecord(asRecord(division).ext).principalName);
  const preservedName = decodePreservedPrincipalSelection(preservedSelection);
  if (!preservedSelection || !preservedName) {
    return options;
  }

  if (options.some((option) => option.value === preservedSelection)) {
    return options;
  }

  return [
    {
      label: `${preservedName} (preserved from onboarding)`,
      value: preservedSelection,
    },
    ...options,
  ];
};

export {
  PRESERVED_PRINCIPAL_SELECTION_PREFIX,
};
